import React from 'react';
import classes from './ExaminationsTab.module.css';

import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { fetchExaminationByID, selectExaminations } from '../../../../store/slices/examinations';
import Spinner from '../../Spinner/Spinner';
import { ExaminationAudio } from '../../../ExaminationAudio/ExaminationAudio';
import { format } from 'date-fns';
import downloadIcon from '../../../../assets/icons/download-icon.svg';
import image from '../../../../assets/images/heart-auscultation.png';
import { Accordion } from '../../Accordion/Accordion';
import { IDocument } from '../../../../store/slices/documents';
import { useTranslation } from '../../../../hooks/useTranslations';

type Props = {
  startExamination: (examId: number, callback: (value: string) => void) => void;
  report?: boolean;
};

const HeartAuscultationExamination: React.FC<Props> = ({ report }) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const [examinationId, setExaminationId] = React.useState<string>();
  const examinations = useAppSelector(selectExaminations);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [attachments, setAttachments] = React.useState<IDocument[]>([]);
  const [updateAttachments, setUpdateAttachments] = React.useState<boolean>(false);
  const [isFetched, setIsFetched] = React.useState<boolean>(false);

  const examination = React.useMemo(() => {
    const _exam = examinations.find(
      (examination) => examination.examination_name === 'Heart Auscultation'
    );
    if (_exam) {
      setExaminationId(_exam.work_order_examination_id);
      setAttachments(_exam?.attachments ?? []);
    }
    return _exam;
  }, [examinations]);

  const hasStartedExamination = React.useMemo(() => {
    return !!examinations.find((examination) => examination.work_order_examination_id);
  }, [examinations]);

  const fetchData = async () => {
    setLoading(true);
    if (examinationId) {
      await dispatch(fetchExaminationByID(examinationId));
      setUpdateAttachments(true);
      setIsFetched(true);
      return;
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (updateAttachments) {
      setAttachments(examination?.attachments ?? []);
      setUpdateAttachments(false);
      setLoading(false);
    }
  }, [updateAttachments]);

  return (
    <Accordion
      disableExpand={!examination?.attachments || examination?.attachments?.length === 0}
      header={
        <div className={classes.reportTabHeader}>
          <div className={classes.headerName}>
            <div className={classes.reportTabHeaderMain}>
              {translations('examinations.heart.auscultation')}
            </div>
            {attachments.length === 0 && !isFetched && (
              <div
                className={classes.downloadButton}
                style={{
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (!loading) {
                    fetchData();
                  }
                }}
              >
                {loading ? (
                  <span>{translations('examinations.loading')}</span>
                ) : (
                  <span>{translations('examinations.fetch')}</span>
                )}
                {!loading && (
                  <img className={classes.downloadIcon} src={downloadIcon} alt="download" />
                )}
              </div>
            )}
          </div>
          {hasStartedExamination && (
            <div className={classes.reportTabHeaderSecond}>
              {format(new Date(), 'dd.MM.yyyy')}•{format(new Date(), 'HH:mm')}
            </div>
          )}
        </div>
      }
      containerStyle={{
        boxShadow: 'none',
        border: '1px solid #D7D7D7',
        margin: 10,
      }}
      headerStyle={{
        padding: '16px 10px',
      }}
    >
      <div className={classes.rightContainer}>
        <div className={classes.examinationContainer}>
          {loading ? (
            <Spinner />
          ) : (
            <div className={classes.framesContainer}>
              {attachments.length > 0 && (
                <ExaminationAudio
                  image={image}
                  date={new Date()}
                  attachments={attachments.map((attachment) => attachment.attachment_url)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Accordion>
  );
};

export { HeartAuscultationExamination };
